import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './RecordSession.css';

function RecordSession() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [patientId, setPatientId] = useState('');
  const [patientInfo, setPatientInfo] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    phone: '',
    email: ''
  });
  const [consent, setConsent] = useState(false);
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [medicalEntities, setMedicalEntities] = useState({
    symptoms: [],
    diagnoses: [],
    medications: [],
    procedures: [],
    vitals: []
  });
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showResults, setShowResults] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const streamRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      
      recognition.onresult = (event) => {
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcriptPiece = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcriptPiece + ' ';
          }
        }
        
        if (finalTranscript) {
          setTranscript(prev => prev + finalTranscript);
          extractMedicalInfo(finalTranscript);
        }
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
      };
      
      recognitionRef.current = recognition;
    }

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Already stopped
        }
      }
    };
  }, []);

  const extractMedicalInfo = (text) => {
    const lowerText = text.toLowerCase();
    
    // Extract symptoms
    const symptomKeywords = ['pain', 'ache', 'headache', 'migraine', 'fever', 'cough', 
      'nausea', 'vomiting', 'dizziness', 'fatigue', 'weakness', 'shortness of breath',
      'chest pain', 'abdominal pain', 'sore throat', 'runny nose'];
    
    const newSymptoms = symptomKeywords.filter(symptom => 
      lowerText.includes(symptom) && !medicalEntities.symptoms.includes(symptom)
    );
    
    // Extract medications
    const medicationKeywords = ['aspirin', 'ibuprofen', 'acetaminophen', 'tylenol',
      'lisinopril', 'metformin', 'atorvastatin', 'insulin', 'amoxicillin'];
    
    const newMeds = medicationKeywords.filter(med =>
      lowerText.includes(med) && !medicalEntities.medications.includes(med)
    );
    
    // Extract vitals (basic pattern matching)
    const vitalPatterns = {
      'blood pressure': /blood pressure.*?(\d{2,3}\/\d{2,3})/i,
      'temperature': /temperature.*?(\d{2,3}\.?\d*)/i,
      'heart rate': /heart rate.*?(\d{2,3})/i,
      'respiratory rate': /respiratory rate.*?(\d{1,2})/i
    };
    
    const newVitals = [];
    Object.entries(vitalPatterns).forEach(([vital, pattern]) => {
      const match = text.match(pattern);
      if (match && !medicalEntities.vitals.find(v => v.startsWith(vital))) {
        newVitals.push(`${vital}: ${match[1]}`);
      }
    });
    
    if (newSymptoms.length > 0 || newMeds.length > 0 || newVitals.length > 0) {
      setMedicalEntities(prev => ({
        ...prev,
        symptoms: [...prev.symptoms, ...newSymptoms],
        medications: [...prev.medications, ...newMeds],
        vitals: [...prev.vitals, ...newVitals]
      }));
    }
  };

  const handlePatientInfoChange = (e) => {
    const { name, value } = e.target;
    setPatientInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const startSession = async () => {
    if (!patientInfo.first_name || !patientInfo.last_name || !patientInfo.date_of_birth) {
      alert('Please fill in all required patient information');
      return;
    }

    if (!consent) {
      alert('Patient consent is required to proceed');
      return;
    }

    // Use demo mode - create session ID and proceed
    setSessionId(`DEMO-${Date.now()}`);
    setStep(2);
  };

  const startRecording = async () => {
    try {
      // Check if browser supports getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Your browser does not support audio recording. Please use Chrome, Edge, or Firefox.');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      streamRef.current = stream;
      
      // Start speech recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.start();
          setIsTranscribing(true);
        } catch (error) {
          console.error('Speech recognition error:', error);
          // Continue recording even if speech recognition fails
        }
      }
      
      // Try different MIME types for broader browser support
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/ogg';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = ''; // Let browser choose
          }
        }
      }
      
      const mediaRecorder = new MediaRecorder(stream, 
        mimeType ? { mimeType } : undefined
      );
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        alert('Recording error occurred. Please try again.');
        stopRecording();
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: mimeType || 'audio/webm' 
        });
        const file = new File([audioBlob], 'recording.webm', { 
          type: mimeType || 'audio/webm' 
        });
        setAudioFile(file);
        
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
      
      mediaRecorder.start(1000);
      setRecording(true);
      setRecordingTime(0);
      
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      
      let errorMessage = 'Could not access microphone. ';
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage += 'Please allow microphone access in your browser settings and try again.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage += 'No microphone found. Please connect a microphone and try again.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorMessage += 'Microphone is already in use by another application.';
      } else {
        errorMessage += 'Please check your microphone settings and try again.';
      }
      
      alert(errorMessage);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && recording && !paused) {
      mediaRecorderRef.current.pause();
      setPaused(true);
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && recording && paused) {
      mediaRecorderRef.current.resume();
      setPaused(false);
      if (recognitionRef.current) {
        try {
          recognitionRef.current.start();
        } catch (e) {}
      }
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      setPaused(false);
    }
    
    if (recognitionRef.current && isTranscribing) {
      try {
        recognitionRef.current.stop();
        setIsTranscribing(false);
      } catch (error) {
        console.error('Error stopping recognition:', error);
      }
    }
  };

  const processRecording = async () => {
    if (!audioFile && !transcript) {
      alert('No recording to process');
      return;
    }

    setProcessing(true);
    
    // Simulate AI analysis
    setTimeout(() => {
      setAiAnalysis({
        chiefComplaint: medicalEntities.symptoms[0] || 'General consultation',
        subjective: transcript.substring(0, 200) + '...',
        objective: `Vitals: ${medicalEntities.vitals.join(', ') || 'Not recorded'}`,
        assessment: `Patient presents with: ${medicalEntities.symptoms.join(', ') || 'No specific symptoms mentioned'}`,
        plan: `Recommended medications: ${medicalEntities.medications.join(', ') || 'None mentioned'}. Follow-up in 2 weeks.`,
        confidence: 0.89
      });
      setProcessing(false);
      setShowResults(true);
      setStep(3);
    }, 2000);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const saveSession = async () => {
    // Save to localStorage
    const newSession = {
      id: Date.now(),
      patient_first_name: patientInfo.first_name,
      patient_last_name: patientInfo.last_name,
      created_at: new Date().toISOString(),
      status: 'completed',
      duration_seconds: recordingTime,
      transcript: transcript,
      analysis: aiAnalysis
    };
    
    const existingSessions = localStorage.getItem('clinical_sessions');
    let sessions = [];
    if (existingSessions) {
      try {
        sessions = JSON.parse(existingSessions);
      } catch (e) {
        sessions = [];
      }
    }
    
    sessions.unshift(newSession); // Add to beginning
    localStorage.setItem('clinical_sessions', JSON.stringify(sessions));
    
    alert('Session saved successfully!');
    navigate('/dashboard');
  };

  return (
    <div className="record-session-container">
      <div className="record-session-header">
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1>Clinical Conversation Recording</h1>
        <div className="step-indicator">
          <div className={`step ${step >= 1 ? 'active' : ''}`}>1. Patient Info</div>
          <div className={`step ${step >= 2 ? 'active' : ''}`}>2. Record</div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>3. Review</div>
        </div>
      </div>

      <div className="record-session-content">
        {step === 1 && (
          <div className="patient-info-form">
            <div className="form-card">
              <h2>Patient Information</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={patientInfo.first_name}
                    onChange={handlePatientInfoChange}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={patientInfo.last_name}
                    onChange={handlePatientInfoChange}
                    placeholder="Enter last name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date of Birth *</label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={patientInfo.date_of_birth}
                    onChange={handlePatientInfoChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select
                    name="gender"
                    value={patientInfo.gender}
                    onChange={handlePatientInfoChange}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={patientInfo.phone}
                    onChange={handlePatientInfoChange}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={patientInfo.email}
                    onChange={handlePatientInfoChange}
                    placeholder="patient@email.com"
                  />
                </div>
              </div>

              <div className="consent-section">
                <div className="consent-box">
                  <h3>📋 HIPAA Consent & Recording Authorization</h3>
                  <div className="consent-text">
                    <p>
                      I understand and consent to the following:
                    </p>
                    <ul>
                      <li>This clinical conversation will be recorded and transcribed</li>
                      <li>The recording and transcript will be stored securely and encrypted</li>
                      <li>AI-powered analysis will be used to extract medical information</li>
                      <li>Only authorized healthcare providers will have access to this data</li>
                      <li>All data handling complies with HIPAA regulations</li>
                    </ul>
                  </div>
                  <label className="consent-checkbox">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                    />
                    <span>I have read and agree to the above terms *</span>
                  </label>
                </div>
              </div>

              <button
                className="primary-button"
                onClick={startSession}
                disabled={!consent || !patientInfo.first_name || !patientInfo.last_name || !patientInfo.date_of_birth}
              >
                Start Session
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="recording-panel">
            <div className="recording-grid">
              <div className="recording-controls">
                <div className="recording-status">
                  {recording && (
                    <div className="recording-indicator">
                      <span className="pulse-dot"></span>
                      <span>Recording in Progress</span>
                    </div>
                  )}
                  {!recording && !audioFile && (
                    <div className="ready-indicator">
                      <span>Ready to Record</span>
                    </div>
                  )}
                  {!recording && audioFile && (
                    <div className="completed-indicator">
                      <span>✓ Recording Complete</span>
                    </div>
                  )}
                </div>

                <div className="timer">
                  {formatTime(recordingTime)}
                </div>

                <div className="control-buttons">
                  {!recording && !audioFile && (
                    <button className="record-button" onClick={startRecording}>
                      <span className="button-icon">🎙️</span>
                      Start Recording
                    </button>
                  )}
                  
                  {recording && !paused && (
                    <>
                      <button className="pause-button" onClick={pauseRecording}>
                        <span className="button-icon">⏸️</span>
                        Pause
                      </button>
                      <button className="stop-button" onClick={stopRecording}>
                        <span className="button-icon">⏹️</span>
                        Stop
                      </button>
                    </>
                  )}

                  {recording && paused && (
                    <>
                      <button className="resume-button" onClick={resumeRecording}>
                        <span className="button-icon">▶️</span>
                        Resume
                      </button>
                      <button className="stop-button" onClick={stopRecording}>
                        <span className="button-icon">⏹️</span>
                        Stop
                      </button>
                    </>
                  )}

                  {!recording && audioFile && (
                    <button className="process-button" onClick={processRecording} disabled={processing}>
                      {processing ? (
                        <>
                          <span className="spinner-small"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <span className="button-icon">🤖</span>
                          Analyze with AI
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="live-panels">
                <div className="transcript-panel">
                  <h3>📝 Live Transcript</h3>
                  <div className="transcript-content">
                    {transcript || 'Transcript will appear here as you speak...'}
                  </div>
                </div>

                <div className="entities-panel">
                  <h3>🏥 Extracted Medical Information</h3>
                  <div className="entities-content">
                    {medicalEntities.symptoms.length > 0 && (
                      <div className="entity-group">
                        <h4>Symptoms</h4>
                        <div className="entity-tags">
                          {medicalEntities.symptoms.map((symptom, index) => (
                            <span key={index} className="tag symptom-tag">{symptom}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {medicalEntities.medications.length > 0 && (
                      <div className="entity-group">
                        <h4>Medications</h4>
                        <div className="entity-tags">
                          {medicalEntities.medications.map((med, index) => (
                            <span key={index} className="tag medication-tag">{med}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {medicalEntities.vitals.length > 0 && (
                      <div className="entity-group">
                        <h4>Vitals</h4>
                        <div className="entity-tags">
                          {medicalEntities.vitals.map((vital, index) => (
                            <span key={index} className="tag vital-tag">{vital}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {medicalEntities.symptoms.length === 0 && 
                     medicalEntities.medications.length === 0 && 
                     medicalEntities.vitals.length === 0 && (
                      <p className="no-entities">Medical entities will be extracted automatically...</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 3 && showResults && aiAnalysis && (
          <div className="results-panel">
            <div className="results-header">
              <h2>🤖 AI Analysis Complete</h2>
              <div className="confidence-badge">
                Confidence: {(aiAnalysis.confidence * 100).toFixed(0)}%
              </div>
            </div>

            <div className="soap-notes">
              <div className="soap-section">
                <h3>Chief Complaint</h3>
                <p>{aiAnalysis.chiefComplaint}</p>
              </div>
              
              <div className="soap-section">
                <h3>Subjective (S)</h3>
                <p>{aiAnalysis.subjective}</p>
                <div className="full-transcript">
                  <details>
                    <summary>View Full Transcript</summary>
                    <p>{transcript}</p>
                  </details>
                </div>
              </div>
              
              <div className="soap-section">
                <h3>Objective (O)</h3>
                <p>{aiAnalysis.objective}</p>
              </div>
              
              <div className="soap-section">
                <h3>Assessment (A)</h3>
                <p>{aiAnalysis.assessment}</p>
              </div>
              
              <div className="soap-section">
                <h3>Plan (P)</h3>
                <p>{aiAnalysis.plan}</p>
              </div>
            </div>

            <div className="results-actions">
              <button className="secondary-button" onClick={() => setStep(2)}>
                ← Edit Recording
              </button>
              <button className="primary-button" onClick={saveSession}>
                💾 Save Session
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RecordSession;
